import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { IoAddOutline, IoArrowForwardOutline, IoCreateOutline, IoTrashOutline } from "react-icons/io5";
import {
  createPage,
  deletePage,
  listPages,
  updatePage,
} from "../../../api/pagesApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import { isServicePageSlug } from "../../../config/cmsServicePages";
import { isIndustryPageSlug } from "../../../config/cmsIndustryPages";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import PageFormModal from "./PageFormModal";

export default function PagesManager() {
  const notify = useNotify();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadPages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listPages();
      setPages(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const filtered = pages.filter((page) => {
    if (isServicePageSlug(page.slug) || isIndustryPageSlug(page.slug)) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      page.name?.toLowerCase().includes(q) ||
      page.slug?.toLowerCase().includes(q)
    );
  });

  async function handleSubmit(payload) {
    setBusy(true);
    try {
      if (editing?.id) {
        await updatePage(editing.id, payload);
        notify.success("Page updated");
      } else {
        await createPage(payload);
        notify.success("Page created");
      }
      setModalOpen(false);
      setEditing(null);
      await loadPages();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deletePage(deleteTarget.id);
      notify.success("Page deleted");
      setDeleteTarget(null);
      await loadPages();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading pages…" />;
  if (error) return <ErrorState message={error} onRetry={loadPages} />;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Looking for service or industry pages?</h3>
            <p className="mt-1 text-sm text-slate-500">
              Service routes and industry verticals are managed in dedicated dashboard sections.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/cmsadmin/dashboard/services"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Service pages
              <IoArrowForwardOutline className="h-4 w-4" />
            </Link>
            <Link
              to="/cmsadmin/dashboard/industries"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Industry pages
              <IoArrowForwardOutline className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">All pages</h2>
          <p className="mt-1 text-sm text-slate-500">
            Home, About, Contact, and other site pages — excluding individual service routes.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
        >
          <IoAddOutline className="h-5 w-5" />
          Add page
        </button>
      </div>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search pages…"
        className="w-full max-w-md rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-200"
      />

      {filtered.length === 0 ? (
        <EmptyState
          title="No pages found"
          description="Create your first page to start managing content."
          action={
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Add page
            </button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50/80">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Slug</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((page) => (
                <tr key={page.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-medium text-slate-900">{page.name}</td>
                  <td className="px-4 py-3 text-slate-500">{page.slug}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                        page.isPublished
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {page.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/cmsadmin/dashboard/pages/${page.id}/sections`}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-slate-700 transition hover:bg-gray-50"
                      >
                        Sections
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(page);
                          setModalOpen(true);
                        }}
                        className="rounded-lg border border-gray-200 p-2 text-slate-600 hover:bg-gray-50"
                        aria-label={`Edit ${page.name}`}
                      >
                        <IoCreateOutline className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(page)}
                        className="rounded-lg border border-gray-200 p-2 text-red-600 hover:bg-red-50"
                        aria-label={`Delete ${page.name}`}
                      >
                        <IoTrashOutline className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PageFormModal
        open={modalOpen}
        initial={editing}
        busy={busy}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete page?"
        message={
          deleteTarget
            ? `This will permanently delete "${deleteTarget.name}".`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
